import { useMemo } from 'react';

import { useDisplayNames } from '@spotify/confidence-core-react';
import { type AvailableMetric } from '@spotify/confidence-editor-react';

import { useStatsData } from './useStatsData';

const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(2)}%`;
};

export const useAvailableMetrics = (): AvailableMetric[] => {
  const { analysisResults } = useStatsData();
  const { displayNames } = useDisplayNames();

  return useMemo(() => {
    if (!analysisResults || !analysisResults[0]?.results) return [];

    const metrics: AvailableMetric[] = [];

    analysisResults[0].results.forEach(metric => {
      if (!metric.id || !metric.result || metric.result.length === 0) return;

      const metricType =
        metric.metricDetails?.metricType === 'METRIC_TYPE_GUARDRAIL'
          ? ('guardrail' as const)
          : ('success' as const);

      const firstResult = metric.result[0];
      if (!firstResult) return;

      const { estimate, lower, upper } =
        firstResult.lastResult?.differenceEstimateRel ?? {};
      const sampleSize = firstResult.sampleSize?.current;
      const conclusion = firstResult.status?.message || 'No data available';
      const status = firstResult.status?.status
        ? String(firstResult.status.status)
        : undefined;
      const hasNoninferiorityMargin = !!metric.metricDetails?.plannedEffectSize;

      metrics.push({
        id: metric.id,
        name: displayNames.get(metric.id) || metric.id,
        type: metricType,
        sampleSize: sampleSize ?? undefined,
        value: formatPercentage(estimate),
        lowerBound: formatPercentage(lower),
        upperBound: formatPercentage(upper),
        conclusion,
        status,
        hasNoninferiorityMargin,
      });
    });

    return metrics;
  }, [analysisResults, displayNames]);
};
